import { ObjectRuntimeState } from '../types'
import { makeObb, obbsOverlap } from './obb'
import { quatFromAxisAngle, quatMultiply } from './quat'

/**
 * 重なり判定の許容ギャップ（studs）。接して静止している面同士は
 * 浮動小数の誤差でわずかに離れて見えるため、その分を吸収する。
 */
const TOUCH_OBJECT_EPSILON = 0.1

/** 実行時の姿勢（Y ヨー(rotationY) ∘ 基準向き(orientation)）。描画と同じ合成。 */
const worldPose = (state: ObjectRuntimeState) =>
  quatMultiply(quatFromAxisAngle('y', state.rotationY), state.orientation)

/** 実行時の姿勢と寸法から、その部品の OBB（回転した直方体）を作る。 */
const worldObb = (state: ObjectRuntimeState) =>
  makeObb(state.position, worldPose(state), {
    x: state.size.x / 2,
    y: state.size.y / 2,
    z: state.size.z / 2,
  })

/**
 * 2 オブジェクトが許容ギャップ込みで重なるか（「〜にふれたら」の判定）。
 *
 * 以前は「回転を包むワールド AABB」の重なりで近似していた。rbxl 由来シーンの
 * 回転はほとんどが 90° 単位（AABB と実体が一致）なので大半は問題なかったが、
 * 斜めに置かれた部品（RJF02 の 45° 傾いたブロック、RJF12 の 20° 傾いた
 * ②あおいブロック など）や、学習者が「まわす」ブロックで回した部品では
 * AABB が実体よりはるかに大きくなり、見た目には離れているのに「ふれた」と
 * 判定されていた。実際の姿勢のまま SAT で厳密に判定する。
 */
export const isAabbTouching = (
  a: ObjectRuntimeState,
  b: ObjectRuntimeState,
): boolean => obbsOverlap(worldObb(a), worldObb(b), TOUCH_OBJECT_EPSILON)

/** クローン名（`元の名前#cloneN`）を判定する接尾辞パターン。 */
const CLONE_NAME_SUFFIX_PATTERN = /^#clone\d+$/

/**
 * 触れたか判定の対象名マッチ。クローンは `元の名前#cloneN` で生成されるため、
 * 完全一致に加えてクローン名も本体扱いにする（Lua の hit.Name 比較は
 * クローン名そのままだが、ブロックの選択肢は元の名前しか出せないため）。
 * `名前#` で始まるだけの別オブジェクトを誤マッチしないよう接尾辞を限定する。
 */
export const matchesTouchTargetName = (
  stateName: string,
  targetName: string,
): boolean =>
  stateName === targetName ||
  (stateName.startsWith(targetName) &&
    CLONE_NAME_SUFFIX_PATTERN.test(stateName.slice(targetName.length)))
